import { faLongArrowAltRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'src/app/hooks';
import { parameterCategories } from 'src/constants/deviceConstants';
import { DeviceValue } from 'src/store/models/Device';
import { Modal } from 'src/store/models/Modal';

import Button from '../Button/Button';
import Table from '../Table/Table';
import { TableHeader } from '../Table/Table.interface';
import classes from './ParametersModal.module.scss';

interface TableParameter {
  name: string;
  category: string;
  oldValue: number;
  newValue: number;
}

export interface ParametersModalProps {
  modal?: Modal;
  className?: string;
}

const ParametersModal: FC<ParametersModalProps> = ({ modal, className }) => {
  const { t } = useTranslation();
  const changedParams = useMemo(
    () => modal?.data as DeviceValue[],
    [modal?.data]
  );

  const deviceDetails = useAppSelector((state) => state.device.deviceDetails);
  const parameters = useAppSelector((state) => state.device.parameters);

  const headers: TableHeader<TableParameter>[] = [
    {
      accessor: 'name',
      label: t('device_parameters:parameter_name'),
    },
    {
      accessor: 'category',
      label: t('device_parameters:category'),
    },
    {
      accessor: 'newValue',
      label: t('device_parameters:old_new_value'),
      component: (param) => {
        return (
          <div className={classes.OldNew}>
            <p>{param.oldValue}</p>
            <FontAwesomeIcon icon={faLongArrowAltRight} />
            <p>{param.newValue}</p>
          </div>
        );
      },
    },
  ];

  const tableData: TableParameter[] = useMemo(
    () =>
      changedParams?.map((param) => {
        const p = parameters?.find((p) => p.parameterIdRcu === param.id);
        return {
          name: p?.nameToShow,
          category:
            parameterCategories?.find((c) => c?.parameters?.includes(param?.id))
              ?.label || '/',
          oldValue: deviceDetails?.controller?.diagnostic?.parameters?.find(
            (p) => p.id === param.id
          )?.value,
          newValue: param.value,
        };
      }),
    [
      changedParams,
      deviceDetails?.controller?.diagnostic?.parameters,
      parameters,
    ]
  );

  return (
    <div className={classNames(classes.Container, className)}>
      <h4>{modal?.title}</h4>
      <p>{modal?.body}</p>
      <section>
        <div>
          <Table className={classes.Table} headers={headers} data={tableData} />
        </div>
      </section>
      <footer>
        {modal?.secondaryButtonText ? (
          <Button
            className={classes.Secondary}
            onClick={modal?.secondaryAction}
            variant="outlined"
          >
            {modal?.secondaryButtonText}
          </Button>
        ) : null}
        {modal?.primaryButtonText ? (
          <Button className={classes.Primary} onClick={modal?.primaryAction}>
            {modal?.primaryButtonText}
          </Button>
        ) : null}
      </footer>
    </div>
  );
};

export default ParametersModal;
